import { useCallback, useEffect, useState } from "react";
import { ArrowDownLeft, ArrowUpRight, Briefcase, History, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, Field, PageHeader, StatCard } from "@/components/common";
import { MoneyInput, QuickAmounts } from "@/components/MoneyInput";
import { PeriodFilter } from "@/components/PeriodFilter";
import { fmtDateTime, MODAL_LABELS, rupiah } from "@/lib/format";

const PLUS = ["kasbon", "angsuran"];

const HistoryTable = ({ rows, testId }) => (
  rows.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center" data-testid={`${testId}-empty`}>Belum ada mutasi modal.</p> : (
    <div className="overflow-x-auto" data-testid={testId}>
      <Table>
        <TableHeader><TableRow><TableHead>Waktu</TableHead><TableHead>Jenis</TableHead><TableHead>Keterangan</TableHead><TableHead className="text-right">Nominal</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((t) => (
            <TableRow key={t.id}>
              <TableCell className="text-sm whitespace-nowrap">{fmtDateTime(t.created_at)}</TableCell>
              <TableCell className="text-xs font-semibold">{MODAL_LABELS[t.type]}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{t.note}<p className="text-xs">oleh {t.by_name}</p></TableCell>
              <TableCell className={`text-right font-mono ${PLUS.includes(t.type) ? "text-primary" : "text-rose-500"}`}>{PLUS.includes(t.type) ? "+" : "-"}{rupiah(t.amount)}</TableCell>
              <TableCell className="text-right font-mono">{rupiah(t.balance_after)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
);

const MutasiDialog = ({ state, onClose, onSaved }) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setAmount(""); setNote(""); }, [state]);
  if (!state) return null;
  const give = state.kind === "kasbon";
  const submit = async (e) => {
    e.preventDefault();
    if (!Number(amount)) { toast.error("Isi nominal"); return; }
    setBusy(true);
    try {
      await api.post(give ? "/modal/kasbon" : "/modal/return", { petugas_id: state.p.id, amount: Number(amount), note });
      toast.success(give ? `Kas bon ${rupiah(amount)} diberikan ke ${state.p.name}` : `Setoran ${rupiah(amount)} dari ${state.p.name} diterima`);
      onClose(); onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="modal-mutasi-dialog">
        <DialogHeader><DialogTitle className="font-heading">{give ? "Beri Modal / Kas Bon" : "Terima Setor Balik"}</DialogTitle><DialogDescription>{state.p.name} · saldo modal {rupiah(state.p.modal_balance)}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nominal"><MoneyInput value={amount} onChange={setAmount} className="h-14 text-xl" testId="modal-amount-input" /></Field>
          {give ? <QuickAmounts onPick={setAmount} amounts={[500000, 1000000, 2000000, 5000000]} testId="modal-quick" />
            : <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => setAmount(String(state.p.modal_balance))} data-testid="modal-return-all">Setor seluruh saldo</Button>}
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan (opsional)" className="h-11" data-testid="modal-note-input" />
          <DialogFooter><Button type="submit" disabled={busy} className="rounded-full h-11 px-6" data-testid="modal-mutasi-submit">{busy ? "Menyimpan..." : "Simpan"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const HistoryDialog = ({ p, onClose }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { if (p) api.get("/modal/history", { params: { petugas_id: p.id } }).then((r) => setRows(r.data)).catch(() => setRows([])); else setRows(null); }, [p]);
  return (
    <Dialog open={Boolean(p)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="modal-history-dialog">
        <DialogHeader><DialogTitle className="font-heading">Mutasi Modal · {p?.name}</DialogTitle></DialogHeader>
        {rows === null ? <p className="text-sm text-muted-foreground">Memuat...</p> : <HistoryTable rows={rows} testId="modal-history-table" />}
      </DialogContent>
    </Dialog>
  );
};

const PetugasView = ({ me }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/modal/history").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Saldo Modal di Tangan" value={rupiah(me.modal_balance)} icon={Wallet} testId="my-modal-balance" />
        <StatCard label="Kas Bon Diterima" value={rupiah(me.kasbon)} icon={ArrowDownLeft} tone="muted" testId="my-modal-kasbon" />
        <StatCard label="Pencairan Lapangan" value={rupiah(me.pencairan)} icon={ArrowUpRight} tone="muted" testId="my-modal-pencairan" />
        <StatCard label="Angsuran Ditagih" value={rupiah(me.angsuran)} hint={`Setor balik ${rupiah(me.setor_balik)}`} icon={History} tone="muted" testId="my-modal-angsuran" />
      </div>
      <div className="panel"><p className="font-heading font-semibold mb-3">Mutasi Modal Saya</p><HistoryTable rows={rows} testId="my-modal-history" /></div>
    </div>
  );
};

const Line = ({ label, value, tone = "" }) => (
  <div className="flex items-center justify-between py-1.5 text-sm"><span className="text-muted-foreground">{label}</span><span className={`font-mono font-semibold ${tone}`}>{value}</span></div>
);

const OfficerTable = ({ items, canEdit, onAct, onHistory }) => (
  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="modal-officer-table">
    {items.map((p) => (
      <div key={p.id} className="panel !p-5 min-w-0 flex flex-col" data-testid={`modal-row-${p.id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0"><p className="font-heading font-semibold truncate">{p.name}</p><p className="text-xs text-muted-foreground truncate">{p.resort_name || "Tanpa resort"}</p></div>
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-full shrink-0" onClick={() => onHistory(p)} aria-label="Riwayat" data-testid={`modal-history-${p.id}`}><History className="h-4 w-4" /></Button>
        </div>
        <div className="mt-3 rounded-2xl bg-primary/10 p-3 text-center">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Saldo Modal</p>
          <p className="font-heading text-xl font-bold text-primary tabular-nums" data-testid={`modal-balance-${p.id}`}>{rupiah(p.modal_balance)}</p>
        </div>
        <div className="mt-3 divide-y">
          <Line label="Kas Bon" value={rupiah(p.kasbon)} />
          <Line label="Pencairan" value={`-${rupiah(p.pencairan)}`} tone="text-rose-500" />
          <Line label="Angsuran" value={rupiah(p.angsuran)} />
          <Line label="Setor Balik" value={`-${rupiah(p.setor_balik)}`} tone="text-rose-500" />
        </div>
        {canEdit && (
          <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-2">
            <Button size="sm" className="h-9 rounded-full" onClick={() => onAct({ kind: "kasbon", p })} data-testid={`modal-kasbon-${p.id}`}>Kas Bon</Button>
            <Button size="sm" variant="outline" className="h-9 rounded-full" disabled={!p.modal_balance} onClick={() => onAct({ kind: "return", p })} data-testid={`modal-return-${p.id}`}>Terima Setoran</Button>
          </div>
        )}
      </div>
    ))}
  </div>
);

const CashSummary = ({ items }) => (
  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6" data-testid="modal-cash-summary">
    {items.map((p) => {
      const diff = p.kasbon + p.angsuran - p.pencairan - p.setor_balik;
      return (
        <div key={p.id} className="stat-card" data-testid={`modal-cash-card-${p.id}`}>
          <p className="eyebrow mb-3">{p.name} · Setoran vs Tarikan Modal</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <p>Tarikan modal<br /><b className="font-mono">{rupiah(p.kasbon)}</b></p>
            <p>Setor balik<br /><b className="font-mono">{rupiah(p.setor_balik)}</b></p>
            <p>Pencairan<br /><b className="font-mono">{rupiah(p.pencairan)}</b></p>
            <p>Angsuran<br /><b className="font-mono">{rupiah(p.angsuran)}</b></p>
          </div>
          <p className="mt-3 pt-3 border-t flex justify-between text-sm font-semibold">Selisih kas lapangan<span className={`font-mono ${diff < 0 ? "text-rose-500" : "text-primary"}`}>{rupiah(diff)}</span></p>
        </div>
      );
    })}
  </div>
);

export default function ModalPage() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [act, setAct] = useState(null);
  const [hist, setHist] = useState(null);
  const [range, setRange] = useState(null);
  const canEdit = ["kasir", "admin_cabang"].includes(user.role);
  const load = useCallback(() => { if (range) api.get("/modal", { params: range }).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))); }, [range]);
  useEffect(() => { load(); }, [load]);
  const total = (items || []).reduce((s, p) => s + p.kasbon + p.angsuran - p.pencairan - p.setor_balik, 0);

  return (
    <div>
      <PageHeader title="Modal Petugas Lapangan"
        action={user.role !== "petugas" && items && <div className="rounded-2xl border bg-card px-4 py-2.5" data-testid="modal-total"><p className="eyebrow">Total modal di lapangan (periode)</p><p className="font-mono font-semibold">{rupiah(total)}</p></div>} />
      <div className="mb-6"><PeriodFilter onChange={setRange} modes={["harian", "tanggal", "bulanan"]} testId="modal-period" /></div>
      {items && user.role !== "petugas" && items.length > 0 && <CashSummary items={items} />}
      {items === null ? <p className="text-muted-foreground">Memuat...</p> : user.role === "petugas" ? (items[0] ? <PetugasView me={items[0]} /> : null) : items.length === 0 ? (
        <EmptyState icon={Briefcase} title="Belum ada petugas lapangan" text="Tambahkan petugas lapangan melalui menu Resort atau Kelola Petugas." testId="modal-empty" />
      ) : <OfficerTable items={items} canEdit={canEdit} onAct={setAct} onHistory={setHist} />}
      <MutasiDialog state={act} onClose={() => setAct(null)} onSaved={load} />
      <HistoryDialog p={hist} onClose={() => setHist(null)} />
    </div>
  );
}
